/**
 * The Admin guide's building blocks, all on the tutorial's motion: Flow (a
 * pipeline whose steps light in turn, in an Illustration), Points (short
 * lines), Rows (a table whose rows fade up in turn) and Tag (who acts: the
 * engine by itself, a person, or your rules). Reduced motion sees the end.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, Fragment, ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';

import { Badge } from '@/components/ui';

import { Illustration } from '../../tutorial/motion/Illustration';
import { Enter, Node } from '../../tutorial/motion/Node';

export interface FlowStep {
  when: ReactNode;
  what: string;
  how?: ReactNode;
}

export const Flow: FC<{ steps: FlowStep[]; stamp: string }> = ({ steps, stamp }) => (
  <Illustration cycleSeconds={steps.length * 1.6 + 2} stamp={stamp}>
    <div className="flex flex-wrap items-stretch gap-2">
      {steps.map((s, i) => (
        <Fragment key={s.what}>
          {i > 0 ? <ArrowRight className="h-4 w-4 shrink-0 self-center text-muted-foreground/60" aria-hidden /> : null}
          <Node at={i} tickSeconds={1.6} className="min-w-[10rem] max-w-[15rem] flex-1 flex-col items-start gap-0.5 whitespace-normal">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">{s.when}</span>
            <span className="text-[13px] font-semibold text-foreground">{s.what}</span>
            {s.how ? <span className="text-[11.5px] leading-snug text-muted-foreground">{s.how}</span> : null}
          </Node>
        </Fragment>
      ))}
    </div>
  </Illustration>
);

export const Points: FC<{ items: ReactNode[] }> = ({ items }) => (
  <ul className="flex flex-col gap-1.5 text-sm">
    {items.map((item, i) => (
      <li key={i} className="flex gap-2">
        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary/70" aria-hidden />
        <span className="text-foreground/90">{item}</span>
      </li>
    ))}
  </ul>
);

export const Rows: FC<{ head: string[]; rows: ReactNode[][]; stamp: string }> = ({ head, rows, stamp }) => (
  <Illustration cycleSeconds={rows.length * 0.5 + 6} stamp={stamp}>
    <div className="grid gap-x-4 text-[13px]" style={{ gridTemplateColumns: `minmax(11rem,1fr) repeat(${head.length - 1}, minmax(9rem,2fr))` }}>
      {head.map((h) => (
        <span key={h} className="border-b border-border/60 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</span>
      ))}
      {rows.map((cells, r) => (
        <Enter key={r} at={r * 0.5} className="col-span-full grid grid-cols-subgrid border-b border-border/40 py-1.5">
          {cells.map((cell, c) => (
            <span key={c} className={c === 0 ? 'font-medium' : 'text-foreground/85'}>{cell}</span>
          ))}
        </Enter>
      ))}
    </div>
  </Illustration>
);

const TAG = { auto: { tone: 'success', label: 'by itself' }, person: { tone: 'warning', label: 'a person' }, rules: { tone: 'destructive', label: 'your rules' } } as const;

export const Tag: FC<{ kind: keyof typeof TAG; label?: string }> = ({ kind, label }) => (
  <Badge tone={TAG[kind].tone} className="ml-1.5 align-middle">{label ?? TAG[kind].label}</Badge>
);

/** A live engine value, bold so the eye finds what the settings below change. */
export const V: FC<{ children: ReactNode }> = ({ children }) => <b className="font-semibold text-foreground">{children}</b>;
